import { useCallback, useEffect, useState } from 'react';
import { api, fmt, fmtDate, num, poNo, type User } from '../api.ts';
import DataTable, { type Column } from '../components/DataTable.tsx';
import SearchSelect from '../components/SearchSelect.tsx';
import { useList } from '../lib/useList.ts';

interface Row {
  id: number; name: string; code: string | null; contact_name: string | null;
  phone: string | null; email: string | null; gst_number: string | null;
  order_count: string; preset_count: string;
}

interface Detail {
  summary: {
    customer_id: number; customer_name: string; code: string | null;
    contact_name: string | null; phone: string | null; email: string | null;
    address: string | null; gst_number: string | null; active: boolean;
    order_count: string; ordered_quantity: string; ordered_value: string;
    dispatched_quantity: string; dispatched_value: string;
    paid_value: string; outstanding: string;
    first_order_date: string | null; last_order_date: string | null;
    oldest_dispatch_date: string | null; last_payment_date: string | null;
    preset_count: string; open_jobs: string;
  };
  orders: {
    id: number; so_number: number; po_number: string | null; job_numbers: string | null;
    po_date: string; delivery_date: string | null;
    priority: string; status: string; delivery_state: string;
    item_count: string; order_value: string;
  }[];
  presets: {
    id: number; name: string; code: string | null;
    box_type: string | null; box_size: string | null; ups: number | null;
    board_specification: string | null; sheet_size: string | null; gsm: string | null;
    board_item_id: number | null; board_name: string | null;
  }[];
  jobs: { job_number: string; status: string; ordered_quantity: string;
          production_quantity: string; item_name: string }[];
}

const rupees = (v: unknown) => `₹${fmt(v as string)}`;
const blank = { name: '', code: '', contactName: '', phone: '', email: '', address: '', gstNumber: '' };
const blankPreset = {
  name: '', code: '', boxType: '', boxSize: '', ups: '',
  boardItemId: '',
};

export default function Customers({ user }: { user: User }) {
  const list = useList<Row>('/api/customers', 'customers', { sort: 'name' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...blank });
  const [busy, setBusy] = useState(false);
  const [addingPreset, setAddingPreset] = useState(false);
  const [presetForm, setPresetForm] = useState({ ...blankPreset });
  const [boardOptions, setBoardOptions] = useState<{ item_id: number; name: string; active: boolean }[]>([]);

  // Boards a box template can name, loaded when one is being added.
  useEffect(() => {
    if (!addingPreset || boardOptions.length) return;
    api.get<{ items: { item_id: number; name: string; active: boolean }[] }>('/api/stock/board-items')
      .then((r) => setBoardOptions(r.items.filter((i) => i.active))).catch(() => {});
  }, [addingPreset, boardOptions.length]);

  const canManage = user.role === 'ADMIN' || user.role === 'MANAGER';

  // Fetching, paging and filtering all live in the shared hook now.
  const load = list.reload;

  const openDetail = useCallback(async (id: number) => {
    setOpenId(id); setDetail(null); setEditing(false); setError('');
    try {
      setDetail(await api.get<Detail>(`/api/customers/${id}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that customer');
    }
  }, []);

  async function create() {
    setBusy(true); setError('');
    try {
      const res = await api.post<{ customer: { id: number; name: string } }>('/api/customers', {
        name: form.name.trim(),
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
        ...(form.contactName.trim() ? { contactName: form.contactName.trim() } : {}),
        ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
        ...(form.email.trim() ? { email: form.email.trim() } : {}),
        ...(form.address.trim() ? { address: form.address.trim() } : {}),
        ...(form.gstNumber.trim() ? { gstNumber: form.gstNumber.trim() } : {}),
      });
      setNotice(`${res.customer.name} added`);
      setForm({ ...blank });
      setAdding(false);
      await load();
      await openDetail(res.customer.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that customer');
    } finally { setBusy(false); }
  }

  async function saveEdit() {
    if (openId === null) return;
    setBusy(true); setError('');
    try {
      await api.patch(`/api/customers/${openId}`, {
        name: form.name.trim(),
        code: form.code.trim() || null,
        contactName: form.contactName.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        gstNumber: form.gstNumber.trim() || null,
      });
      setNotice('Customer updated');
      setEditing(false);
      await load();
      await openDetail(openId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save those changes');
    } finally { setBusy(false); }
  }

  function startEdit() {
    if (!detail) return;
    const s = detail.summary;
    setForm({
      name: s.customer_name, code: s.code ?? '', contactName: s.contact_name ?? '',
      phone: s.phone ?? '', email: s.email ?? '', address: s.address ?? '',
      gstNumber: s.gst_number ?? '',
    });
    setEditing(true);
    setNotice('');
  }

  const f = (k: keyof typeof blank) => (e: { target: { value: string } }) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  /**
   * Pre-registering a box this way is the one thing automatic pre-fill from
   * order history can never do on its own: there is no history yet for a box
   * nobody has ordered.
   */
  async function createPreset() {
    if (openId === null) return;
    setBusy(true); setError('');
    try {
      await api.post(`/api/customers/${openId}/presets`, {
        name: presetForm.name.trim(),
        ...(presetForm.code.trim() ? { code: presetForm.code.trim() } : {}),
        ...(presetForm.boxType.trim() ? { boxType: presetForm.boxType.trim() } : {}),
        ...(presetForm.boxSize.trim() ? { boxSize: presetForm.boxSize.trim() } : {}),
        ...(presetForm.ups ? { ups: Number(presetForm.ups) } : {}),
        ...(presetForm.boardItemId ? { boardItemId: Number(presetForm.boardItemId) } : {}),
      });
      setNotice(`${presetForm.name} saved as a box template`);
      setPresetForm({ ...blankPreset });
      setAddingPreset(false);
      await openDetail(openId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that box template');
    } finally { setBusy(false); }
  }

  // A discontinued box: stop suggesting it, without touching any order that
  // already used it.
  async function deactivatePreset(presetId: number) {
    if (openId === null) return;
    if (!confirm('Remove this box from the "pick from saved boxes" list? ' +
                  'Past orders that used it are not affected.')) return;
    try {
      await api.patch(`/api/customers/${openId}/presets/${presetId}`, { active: false });
      await openDetail(openId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that box template');
    }
  }

  const pf = (k: keyof typeof blankPreset) => (e: { target: { value: string } }) =>
    setPresetForm((p) => ({ ...p, [k]: e.target.value }));

  const fields = (
    <div className="row2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
      <div className="field"><label className="req">Company name</label>
        <input type="text" value={form.name} onChange={f('name')} /></div>
      <div className="field"><label>Short code</label>
        <input type="text" value={form.code} onChange={f('code')} /></div>
      <div className="field"><label>Contact person</label>
        <input type="text" value={form.contactName} onChange={f('contactName')} /></div>
      <div className="field"><label>Phone</label>
        <input type="text" value={form.phone} onChange={f('phone')} /></div>
      <div className="field"><label>Email</label>
        <input type="text" value={form.email} onChange={f('email')} /></div>
      <div className="field"><label>GST number</label>
        <input type="text" value={form.gstNumber} onChange={f('gstNumber')} /></div>
      <div className="field" style={{ gridColumn: '1 / -1' }}><label>Address</label>
        <input type="text" value={form.address} onChange={f('address')} /></div>
    </div>
  );

  // ------------------------------------------------------------- detail
  if (openId !== null) {
    return (
      <div className="panel">
        <div className="panel-head">
          <button className="btn" onClick={() => { setOpenId(null); setDetail(null); }}>
            Back
          </button>
          <span className="count">{detail?.summary.customer_name ?? 'Loading…'}</span>
          <div className="spacer" />
          {canManage && detail && !editing && (
            <button className="btn" onClick={startEdit}>Edit details</button>
          )}
          {editing && (
            <>
              <button className="btn" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
              <button className="btn primary" disabled={busy || !form.name.trim()}
                      onClick={saveEdit}>{busy ? 'Saving…' : 'Save'}</button>
            </>
          )}
        </div>

        {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
        {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

        {!detail ? <div className="empty">Loading…</div> : (
          <div style={{ padding: 16 }}>
            {editing ? fields : (
              <div className="hint" style={{ marginBottom: 16 }}>
                {[detail.summary.code, detail.summary.contact_name, detail.summary.phone,
                  detail.summary.email, detail.summary.gst_number, detail.summary.address]
                  .filter(Boolean).join(' · ') || 'No contact details recorded.'}
              </div>
            )}

            <div style={{ display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                          gap: 12, marginBottom: 18 }}>
              <Stat label="Orders" value={fmt(detail.summary.order_count)}
                    hint={detail.summary.first_order_date
                      ? `since ${fmtDate(detail.summary.first_order_date)}` : undefined} />
              <Stat label="Ordered" value={rupees(detail.summary.ordered_value)} hint="pre-GST" />
              <Stat label="Dispatched" value={rupees(detail.summary.dispatched_value)} hint="pre-GST" />
              <Stat label="Received" value={rupees(detail.summary.paid_value)}
                    hint={detail.summary.last_payment_date
                      ? `last ${fmtDate(detail.summary.last_payment_date)}` : 'nothing yet'} />
              <Stat label="Due to us"
                    value={num(detail.summary.outstanding) < 0
                      ? `${rupees(Math.abs(num(detail.summary.outstanding)))} advance`
                      : rupees(detail.summary.outstanding)} hint="pre-GST" />
              <Stat label="Open jobs" value={fmt(detail.summary.open_jobs)} />
              <Stat label="Saved boxes" value={fmt(detail.summary.preset_count)}
                    hint="repeat-order templates" />
            </div>

            <h3 style={{ margin: '0 0 10px' }}>Open jobs</h3>
            {detail.jobs.length === 0 ? (
              <div className="hint" style={{ marginBottom: 18 }}>Nothing in production.</div>
            ) : (
              <table style={{ marginBottom: 18 }}>
                <thead>
                  <tr><th>Job</th><th>Box</th><th className="num">Ordered</th>
                      <th className="num">Running</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {detail.jobs.map((j) => (
                    <tr key={j.job_number}>
                      <td>{j.job_number}</td>
                      <td>{j.item_name}</td>
                      <td className="num">{fmt(j.ordered_quantity)}</td>
                      <td className="num dim">{fmt(j.production_quantity)}</td>
                      <td className="dim">{j.status.replace('_', ' ').toLowerCase()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h3 style={{ margin: '0 0 10px' }}>Orders</h3>
            {detail.orders.length === 0 ? (
              <div className="hint">No orders yet.</div>
            ) : (
              <div className="tbl">
                <table>
                  <thead>
                    <tr><th>Sales order</th><th>Customer PO</th><th>Jobs</th><th>Date</th><th>Due</th>
                        <th className="num">Lines</th>
                        <th className="num">Value</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {detail.orders.map((o) => (
                      <tr key={o.id}
                          className={o.delivery_state === 'LATE' ? 'late'
                                   : o.delivery_state === 'DUE_SOON' ? 'soon' : ''}>
                        <td className="mono">SO{o.so_number}</td>
                        <td className={o.po_number ? undefined : 'dim'}>{poNo(o.po_number)}</td>
                        <td>
                          {o.job_numbers
                            ? <span className="mono">{o.job_numbers}</span>
                            : <span className="dim">no card yet</span>}
                        </td>
                        <td className="dim">{fmtDate(o.po_date)}</td>
                        <td className="dim">{fmtDate(o.delivery_date)}</td>
                        <td className="num dim">{o.item_count}</td>
                        <td className="num">{rupees(o.order_value)}</td>
                        <td className="dim">{o.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', margin: '18px 0 10px' }}>
              <h3 style={{ margin: 0 }}>Saved boxes</h3>
              {canManage && (
                <button className="btn" style={{ marginLeft: 'auto' }}
                        onClick={() => { setError(''); setAddingPreset((v) => !v); }}>
                  {addingPreset ? 'Cancel' : '+ Add box template'}
                </button>
              )}
            </div>
            <div className="hint" style={{ marginBottom: 10 }}>
              Repeat orders fill themselves in from these automatically. Save
              one here ahead of time for a box agreed with the customer but
              not yet ordered — otherwise there's nothing to fill in until the
              first real order creates it.
            </div>

            {addingPreset && (
              <div className="subpanel" style={{ marginBottom: 14 }}>
                <div className="row2" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                  <div className="field"><label className="req">Box name</label>
                    <input type="text" value={presetForm.name} onChange={pf('name')} /></div>
                  <div className="field"><label>Code</label>
                    <input type="text" value={presetForm.code} onChange={pf('code')} /></div>
                  <div className="field"><label>Box type</label>
                    <input type="text" value={presetForm.boxType} onChange={pf('boxType')} /></div>
                  <div className="field"><label>Box size</label>
                    <input type="text" value={presetForm.boxSize} onChange={pf('boxSize')} /></div>
                  <div className="field"><label>Ups</label>
                    <input type="number" min="1" value={presetForm.ups} onChange={pf('ups')} /></div>
                  {/* The board comes from Board stock, so its cards are filled in from it. */}
                  <div className="field" style={{ gridColumn: 'span 3' }}><label>Board</label>
                    <SearchSelect id="preset-board" value={presetForm.boardItemId}
                                  placeholder="Type to search, e.g. 230 or 61" emptyLabel="Choose the board…"
                                  onChange={(v) => setPresetForm((p) => ({ ...p, boardItemId: v }))}
                                  options={boardOptions.map((o) => ({ value: String(o.item_id), label: o.name }))} />
                  </div>
                </div>
                <button className="btn primary" disabled={busy || !presetForm.name.trim()}
                        onClick={createPreset}>
                  {busy ? 'Saving…' : 'Save box template'}
                </button>
              </div>
            )}

            {detail.presets.length === 0 ? (
              <div className="hint">No saved boxes yet.</div>
            ) : (
              <table>
                <thead>
                  <tr><th>Box</th><th>Type</th><th>Size</th><th className="num">Ups</th>
                      <th>Board</th><th /></tr>
                </thead>
                <tbody>
                  {detail.presets.map((p) => (
                    <tr key={p.id}>
                      <td>{p.name}{p.code && <span className="dim"> · {p.code}</span>}</td>
                      <td className="dim">{p.box_type ?? '—'}</td>
                      <td className="dim">{p.box_size ?? '—'}</td>
                      <td className="num dim">{p.ups ?? '—'}</td>
                      <td className="dim">
                        {p.board_name
                          ?? ([p.board_specification, p.sheet_size].filter(Boolean).join(', ') || '—')}
                      </td>
                      <td>
                        {canManage && (
                          <button className="btn" onClick={() => deactivatePreset(p.id)}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  }

  // --------------------------------------------------------------- list
  const columns: Column<Row>[] = [
    { key: 'name', label: 'Company' },
    { key: 'code', label: 'Code', render: (r) => <span className="dim">{r.code ?? '—'}</span> },
    { key: 'contact_name', label: 'Contact', secondary: true,
      render: (r) => <span className="dim">{r.contact_name ?? '—'}</span> },
    { key: 'phone', label: 'Phone', render: (r) => <span className="dim">{r.phone ?? '—'}</span> },
    { key: 'email', label: 'Email', secondary: true,
      render: (r) => <span className="dim">{r.email ?? '—'}</span> },
    { key: 'gst_number', label: 'GST', secondary: true,
      render: (r) => <span className="dim">{r.gst_number ?? '—'}</span> },
    { key: 'order_count', label: 'Orders', type: 'number', align: 'right' },
    {
      key: 'open', label: '', sortable: false, filterable: false,
      render: (r) => (
        <button className="btn" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>
          Open
        </button>
      ),
    },
  ];

  return (
    <div className="panel">
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {adding && (
        <div style={{ padding: 16, borderBottom: '1px solid var(--line)' }}>
          {fields}
          <button className="btn primary" disabled={busy || !form.name.trim()} onClick={create}>
            {busy ? 'Saving…' : 'Add customer'}
          </button>
        </div>
      )}

      <DataTable
        list={list}
        columns={columns}
        exportName="/api/customers"
        rowKey={(r) => r.id}
        onRowClick={(r) => openDetail(r.id)}
        empty={`No customers yet.${canManage ? ' Add the first one to start taking orders.' : ''}`}
        toolbar={canManage && (
          <button className="btn primary" onClick={() => { setNotice(''); setAdding((v) => !v); }}>
            {adding ? 'Cancel' : 'New customer'}
          </button>
        )}
      />
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {hint && <div className="stat-hint">{hint}</div>}
    </div>
  );
}
